import { useState, type ReactNode } from 'react';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { useNavigate } from 'react-router-dom';

import {
  activeAdminTenantId,
  activeParticipantMembership,
  adminTenants,
  participantTenants,
  useAuth,
  userHasRole,
} from '../auth/AuthContext';
import { pinTenant } from '../auth/tenantPin';
import { useThemeMode } from '../theme/ThemeModeContext';
import { useSelectedTenant } from './SelectedTenantContext';
import { HeaderMediaPrep } from './HeaderMediaPrep';

/**
 * Top app bar. Contains:
 *   - Brand title on the left
 *   - Dark-mode toggle button
 *   - Avatar that opens a menu: Profile / Help / Sign out
 *
 * The `leftSlot` slot is for the mobile-drawer hamburger that the
 * Layout component injects (only visible below the md breakpoint).
 */
export function TopBar({ leftSlot }: { leftSlot?: ReactNode }) {
  const { resolvedMode, toggle } = useThemeMode();
  const { user, logout } = useAuth();
  const { name: selectedTenantName } = useSelectedTenant();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // Anchor for the tenant-switcher dropdown. Separate from the
  // account-menu anchor above so opening one doesn't close the other.
  const [tenantSwitcherAnchor, setTenantSwitcherAnchor] =
    useState<HTMLElement | null>(null);

  // Tenants the user can switch between. Falls back to participant
  // memberships when the user holds no admin role anywhere — pure
  // participants in 2+ tenants get the same switcher experience as
  // multi-tenant admins. Mixed-role users (admin somewhere AND
  // participant elsewhere) get the admin set because that's the
  // role-aware Dashboard branch they land on.
  const adminMemberships = adminTenants(user);
  const participantMemberships = participantTenants(user);
  const tenantSwitcherMemberships =
    adminMemberships.length > 0 ? adminMemberships : participantMemberships;
  const activeAdminId = activeAdminTenantId(user);
  const activeParticipantId = activeParticipantMembership(user)?.tenant_id ?? null;
  // The id that's currently selected — admin context wins when both
  // role types are present, mirroring the Dashboard's dispatch
  // hierarchy.
  const activeSwitcherId =
    adminMemberships.length > 0 ? activeAdminId : activeParticipantId;

  // Title suffix: "10NEWTONS — Tenant Name".
  //   - tenant_admin / participant: their one tenant comes from their
  //     active membership — always show it.
  //   - superadmin: only show when they've drilled into a tenant
  //     (TenantDetail sets it via SelectedTenantContext); the platform
  //     surfaces themselves aren't tenant-scoped.
  const tenantTitle = (() => {
    if (userHasRole(user, 'superadmin')) return selectedTenantName;
    if (activeAdminId) {
      const m = user?.memberships.find(
        (mm) => mm.tenant_id === activeAdminId && mm.role === 'tenant_admin',
      );
      return m?.tenant_name ?? null;
    }
    return activeParticipantMembership(user)?.tenant_name ?? null;
  })();

  const showTenantSwitcher = tenantSwitcherMemberships.length >= 2;

  const handlePickTenant = (tenantId: string) => () => {
    setTenantSwitcherAnchor(null);
    if (tenantId === activeSwitcherId) return;
    pinTenant(tenantId);
    // Always land on /dashboard so the per-tenant fetches re-run
    // cleanly with the new tenant_id, regardless of which page the
    // operator was on when they switched. A full reload would also
    // work but is jarring; SPA navigation + the active-id change
    // re-renders everything that needs it.
    navigate('/dashboard');
  };

  const handleNav = (path: string) => () => {
    setAnchor(null);
    navigate(path);
  };

  const handleLogout = async () => {
    setAnchor(null);
    await logout();
    navigate('/login', { replace: true });
  };

  const initial =
    user?.full_name?.[0]?.toUpperCase() ?? user?.email?.[0]?.toUpperCase() ?? '?';

  return (
    <AppBar
      position="fixed"
      elevation={0}
      // Version D: a light top bar (paper + ink text + hairline) that pairs
      // with the ink nav rail, rather than the default solid-primary bar.
      // The inherit-coloured icons/menus below then render in ink on light.
      color="default"
      // zIndex above the temporary mobile drawer so the bar stays
      // visible when the drawer is open.
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Toolbar>
        {leftSlot}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexGrow: 1,
          }}
        >
          <Box
            component="img"
            src="/logo-mark.svg"
            alt=""
            sx={{ width: 38, height: 38, display: 'block' }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{ fontWeight: 800, letterSpacing: '0.02em' }}
          >
            10NEWTONS
            {tenantTitle && !showTenantSwitcher && (
              <Typography
                component="span"
                variant="h6"
                sx={{ fontWeight: 400, opacity: 0.85 }}
              >
                {' — '}
                {tenantTitle}
              </Typography>
            )}
          </Typography>
          {tenantTitle && showTenantSwitcher && (
            <>
              <Typography
                component="span"
                variant="h6"
                sx={{ fontWeight: 400, opacity: 0.85, ml: 1 }}
              >
                —
              </Typography>
              {/* Multi-tenant tenant_admin: the title's tenant slot
                  becomes a switcher. Clicking opens a menu listing
                  every tenant_admin tenant the user holds; picking
                  one pins it + navigates to /dashboard so the per-
                  tenant fetches re-run cleanly. */}
              <Button
                color="inherit"
                onClick={(e) =>
                  setTenantSwitcherAnchor(e.currentTarget)
                }
                endIcon={<ExpandMoreIcon />}
                aria-label="Switch tenant"
                sx={{
                  textTransform: 'none',
                  fontWeight: 400,
                  fontSize: 'h6.fontSize',
                  opacity: 0.85,
                  '&:hover': { opacity: 1, bgcolor: 'action.hover' },
                }}
              >
                {tenantTitle}
              </Button>
              <Menu
                anchorEl={tenantSwitcherAnchor}
                open={Boolean(tenantSwitcherAnchor)}
                onClose={() => setTenantSwitcherAnchor(null)}
              >
                {tenantSwitcherMemberships.map((m) => (
                  <MenuItem
                    key={m.tenant_id}
                    onClick={handlePickTenant(m.tenant_id as string)}
                    selected={m.tenant_id === activeSwitcherId}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {m.tenant_id === activeSwitcherId ? (
                        <CheckIcon fontSize="small" />
                      ) : null}
                    </ListItemIcon>
                    {m.tenant_name ?? m.tenant_slug ?? m.tenant_id}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>

        {/* Global "preparing video & audio" progress — shows whenever a
            split/upload's async jobs are in flight for the active tenant. */}
        {activeAdminId && <HeaderMediaPrep tenantId={activeAdminId} />}

        <Tooltip
          title={resolvedMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <IconButton color="inherit" onClick={toggle} aria-label="Toggle dark mode">
            {resolvedMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title={user?.email ?? 'Account'}>
          <IconButton
            onClick={(e) => setAnchor(e.currentTarget)}
            aria-label="Open account menu"
            sx={{ ml: 1 }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
              {initial}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
        >
          {user && (
            <MenuItem disabled sx={{ opacity: '1 !important', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="body2">{user.full_name ?? user.email}</Typography>
              {user.full_name && (
                <Typography variant="caption" color="text.secondary">
                  {user.email}
                </Typography>
              )}
            </MenuItem>
          )}
          {user && <Divider />}
          <MenuItem onClick={handleNav('/profile')}>Profile</MenuItem>
          <MenuItem onClick={handleNav('/help')}>Help</MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>Sign out</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
